import React, { useState, useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import type { Order } from '@hero/schema';
import { getObjectId } from '@hero/schema';
import { getOrderNextAction, getActiveDestination } from '@hero/core';
import { useOrderUpdateApi } from '@hero/api-hooks';
import { toast } from 'sonner';
import { ModalForm } from '@hero/web-components';
import { LocationIcon } from '@hero/web-icons';
import { Badge } from '../../../ui/Badge';
import {
  OrderImageUploader,
  buildOrderImageUpdatePayload,
} from '../Photo/OrderImageUploader';

interface OrderUpdateStatusModalProps {
  order: Order;
  isOpen: boolean;
  onClose: () => void;
}

export const OrderUpdateStatusModal: React.FC<OrderUpdateStatusModalProps> = ({
  order,
  isOpen,
  onClose,
}) => {
  const { t } = useTranslation('m-order');
  const nextStep = getOrderNextAction(order.status.order);

  const [files, setFiles] = useState<File[]>([]);
  const [podRef, setPodRef] = useState('');
  const [applyAsOrderPodRef, setApplyAsOrderPodRef] = useState(true);

  const { mutate, isPending } = useOrderUpdateApi({
    onSuccess: () => {
      toast.success(t('toast.success.order_updated'));
    },
    onError: (error: Error) => {
      toast.error(t('toast.error.order_update', { message: error.message }));
    },
  });

  useEffect(() => {
    if (isOpen) {
      setFiles([]);
      setPodRef(order.podRef ?? '');
      setApplyAsOrderPodRef(true);
    }
  }, [isOpen, order.podRef]);

  const nextStatusLabel = t(`actions.order_action_labels.${order.status.order}`, {
    defaultValue: '',
  });

  const { destination: activeDest } = getActiveDestination(order.destinations ?? []);
  const activeDestType = activeDest?.type;
  const activeDestLabel = activeDestType ? t(`destination.${activeDestType}`) : '';
  const activeDestCity =
    activeDest?.address?.city?.long_name || activeDest?.address?.fullAddress || '';
  const activeDestStatus = activeDest ? t(`status.${order.status.order}`) : '';

  const handleSave = useCallback(async () => {
    if (!nextStep) return;

    const imageType = nextStep.image;
    let imagePayload: Awaited<ReturnType<typeof buildOrderImageUpdatePayload>> | undefined;

    if (imageType && files.length > 0) {
      imagePayload = await buildOrderImageUpdatePayload({
        files,
        imageType,
        podRef: nextStep.enterRefNumber ? podRef : undefined,
        applyAsOrderPodRef: nextStep.enterRefNumber ? applyAsOrderPodRef : false,
      });
    }

    mutate(
      {
        _id: getObjectId(order._id),
        action: nextStep.nextAction,
        ...(imagePayload?.newImages.length ? { newImages: imagePayload.newImages } : {}),
        ...(imagePayload?.pod ? { pod: imagePayload.pod } : {}),
      },
      { onSuccess: () => onClose() },
    );
  }, [nextStep, files, podRef, applyAsOrderPodRef, mutate, order._id, onClose]);

  if (!nextStep) return null;

  return (
    <ModalForm
      isOpen={isOpen}
      onClose={onClose}
      title={t('actions.update_status_confirm_title')}
      description={t('actions.update_status_description', { status: nextStatusLabel })}
      onSave={handleSave}
      isLoading={isPending}
      saveText={
        isPending
          ? t('actions.update_status_confirming')
          : t('actions.update_status_confirm')
      }
      size="md">
      <div className="flex flex-col gap-4">
        {activeDest && (
          <div className="flex items-center gap-3 rounded-lg border border-gray-200 bg-gray-50 p-3">
            <div className="flex size-9 shrink-0 items-center justify-center rounded-full bg-primary/10">
              <LocationIcon className="size-4 text-primary" />
            </div>
            <div className="flex min-w-0 flex-1 flex-col gap-0.5">
              <div className="flex items-center gap-2">
                <span className="text-sm font-medium text-gray-900">{activeDestLabel}</span>
                <Badge variant="outline" className="text-xs">
                  {activeDestStatus}
                </Badge>
              </div>
              <span className="truncate text-xs text-gray-500">{activeDestCity}</span>
            </div>
          </div>
        )}

        {nextStep.image && (
          <OrderImageUploader
            files={files}
            onFilesChange={setFiles}
            imageType={nextStep.image}
            podRef={podRef}
            onPodRefChange={setPodRef}
            orderPodRef={order.podRef}
            applyAsOrderPodRef={applyAsOrderPodRef}
            onApplyAsOrderPodRefChange={setApplyAsOrderPodRef}
            showPodRef={!!nextStep.enterRefNumber}
          />
        )}
      </div>
    </ModalForm>
  );
};
